import { useState, useEffect } from "react";
import { createAdminApiClient } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";

const AdminCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCategory, setEditingCategory] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    
    const emptyCategory = {
        name: "",
        slug: "",
        description: "",
        order: 0,
        is_active: true
    };
    
    const [formData, setFormData] = useState(emptyCategory);
    
    const getAdminApi = () => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const credentials = JSON.parse(credStr);
        return createAdminApiClient(credentials.username, credentials.password);
    };
    
    const fetchCategories = async () => {
        try {
            const adminApi = getAdminApi();
            if (!adminApi) {
                setLoading(false);
                return;
            }
            const res = await adminApi.getCategories();
            setCategories(res.data);
        } catch (err) {
            console.error("Failed to fetch categories:", err);
            toast.error("Failed to load categories");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchCategories();
    }, []);
    
    const generateSlug = (name) => {
        return name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };
    
    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData(category);
        setIsDialogOpen(true);
    };
    
    const handleCreate = () => {
        setEditingCategory(null);
        setFormData(emptyCategory);
        setIsDialogOpen(true);
    };
    
    const handleSave = async () => {
        if (!formData.name || !formData.slug) {
            toast.error("Name and slug are required");
            return;
        }
        
        try {
            const adminApi = getAdminApi();
            if (editingCategory) {
                await adminApi.updateCategory(editingCategory.id, formData);
                toast.success("Category updated successfully");
            } else {
                await adminApi.createCategory(formData);
                toast.success("Category created successfully");
            }
            setIsDialogOpen(false);
            fetchCategories();
        } catch (err) {
            console.error("Failed to save category:", err);
            toast.error("Failed to save category");
        }
    };
    
    const handleDelete = async (categoryId) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        
        try {
            const adminApi = getAdminApi();
            await adminApi.deleteCategory(categoryId);
            toast.success("Category deleted");
            fetchCategories();
        } catch (err) {
            console.error("Failed to delete category:", err);
            toast.error("Failed to delete category");
        }
    };
    
    return (
        <div data-testid="admin-categories">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Categories</h1>
                    <p className="text-muted-foreground">Organize products by application type</p>
                </div>
                <Button 
                    onClick={handleCreate}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                    data-testid="create-category-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Category
                </Button>
            </div>
            
            {/* Categories Table */}
            <Card className="bg-card border border-border rounded-none">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : categories.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            No categories yet. Click "Add Category" to create your first category.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border bg-secondary/50">
                                    <TableHead className="font-mono uppercase text-xs w-16">Order</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Name</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Slug</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Description</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Status</TableHead>
                                    <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {categories.map((category) => (
                                    <TableRow key={category.id} className="border-border">
                                        <TableCell className="font-mono text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                <GripVertical className="h-4 w-4" />
                                                {category.order}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-bold">{category.name}</TableCell>
                                        <TableCell className="font-mono text-sm text-muted-foreground">{category.slug}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                                            {category.description || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`rounded-none ${category.is_active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                                {category.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(category)} data-testid={`edit-category-${category.id}`}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            
            {/* Edit/Create Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-card border-border rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl">
                            {editingCategory ? "Edit Category" : "Create Category"}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Name *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => {
                                    const name = e.target.value;
                                    setFormData({ 
                                        ...formData, 
                                        name,
                                        slug: editingCategory ? formData.slug : generateSlug(name)
                                    });
                                }}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                placeholder="Detailing & Automotive"
                                data-testid="category-name"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Slug *</Label>
                            <Input
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                placeholder="detailing-automotive"
                                data-testid="category-slug"
                            />
                            <p className="text-xs text-muted-foreground">URL-friendly identifier. Auto-generated from name.</p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                rows={3}
                                placeholder="Light-duty systems perfect for automotive detailing..."
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Display Order</Label>
                            <Input
                                type="number"
                                value={formData.order}
                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                className="rounded-none bg-input border-transparent focus:border-primary w-24"
                            />
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                            <Label>Active (Visible on Site)</Label>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" className="rounded-none">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none" data-testid="save-category-btn">
                            {editingCategory ? "Save Changes" : "Create Category"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminCategories;
